'use client';

import { ICMSCompatibleComponent } from '@/types/ICMSCompatibleComponent';
import { IFaqItem } from '@/types/IFaqItem';
import { CSSProperties, useEffect, useRef, useState, ChangeEvent } from 'react';
import clsx from 'clsx/lite';
import classes from './index.module.scss';
import XClose from '@/assets/XClose';

export interface IFaqAccordionProps extends Partial<ICMSCompatibleComponent> {
  items: IFaqItem[];
}

interface IFaqItemProps {
  item: IFaqItem;
  index: number;
  isOpen: boolean;
  onToggle: (checked: boolean) => void;
}

function FaqItem(props: IFaqItemProps) {
  const { item, index, isOpen, onToggle } = props;
  const contentRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);
  const id = `faq-item-${index}`;

  useEffect(() => {
    const calculateHeight = () => setHeight(contentRef.current?.scrollHeight || 0);
    calculateHeight();

    window.addEventListener('resize', calculateHeight);
    return () => window.removeEventListener('resize', calculateHeight);
  }, []);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    onToggle(e.target.checked);
  };

  return (
    <details
      open
      className={clsx(classes.faqItem, 'border-primary-aqua relative border-t pt-4 transition-all duration-200')}
    >
      <input
        type='checkbox'
        id={id}
        name={id}
        className={clsx('absolute inset-0 z-10 h-full w-full cursor-pointer opacity-0', classes.controlInput)}
        onChange={handleChange}
        checked={isOpen}
      />
      <summary className={clsx(classes.question, 'cursor-pointer list-none')}>
        <div className='flex items-start justify-between gap-4'>
          <label htmlFor={id} className='text-md flex-1 cursor-pointer font-semibold text-pretty'>
            {item.question}
          </label>
          <span
            className={clsx(
              'text-primary-aqua h-8 w-6 flex-shrink-0 transition-transform duration-500',
              !isOpen && 'rotate-45'
            )}
          >
            <XClose width={32} height={32} />
          </span>
        </div>
      </summary>
      <div
        style={{ '--height': `${height}px` } as CSSProperties}
        className={clsx(classes.answer, 'overflow-hidden transition-all duration-500')}
      >
        <div ref={contentRef} className='pt-4 pb-2'>
          <p className='border-primary-gray/40 border-t pt-2 text-base'>{item.answer}</p>
        </div>
      </div>
    </details>
  );
}

export default function FaqAccordion(props: IFaqAccordionProps) {
  const { editable, items } = props;
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const handleToggle = (index: number, checked: boolean) => {
    setOpenIndex(checked ? index : null);
  };

  return (
    <div {...editable} className='space-y-4'>
      {items?.map((item, index) => (
        <FaqItem
          key={`faq-${index.toString()}`}
          item={item}
          index={index}
          isOpen={openIndex === index}
          onToggle={(checked) => handleToggle(index, checked)}
        />
      ))}
    </div>
  );
}
